import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";
import { useNavigate } from "react-router-dom";
import { useGetRatingsQuery } from "../../store/api/userAPI";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";

interface PromotionFeedbackTableProps {
  showPromotionColumn?: boolean;
  showUserColumn?: boolean;
  userId?: string;
}

export default function PromotionFeedbackTable({
  showPromotionColumn = true,
  showUserColumn = true,
  userId
}: PromotionFeedbackTableProps) {

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  // Fetch ratings
  const { data, isLoading, error, refetch } = useGetRatingsQuery({
    page,
    limit,
    search: searchTerm,
    userId: userId
  }, {
    skip: !userId // Skip query if userId is not available to avoid 401 or bad requests
  });

  const ratings = data?.data?.list || [];
  const totalCount = data?.data?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);

  return (
    <>
      <Loader isLoad={isLoading} />
      <div className="table_header">
        <div className="left_s">
          <SearchBar
            searchTerm={searchTerm}
            setDebouncedSearchTerm={setSearchTerm}
            placeholder="Search..."
          />
        </div>
      </div>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow hover>
              {showUserColumn && <TableCell>User Detail</TableCell>}
              {showPromotionColumn && <TableCell>Order Id</TableCell>}
              <TableCell>Rating</TableCell>
              <TableCell>Feedback Provided</TableCell>
              <TableCell>Date & Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ratings.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No ratings found</TableCell>
              </TableRow>
            ) : (
              ratings.map((row) => (
                <TableRow hover key={row._id}>
                  {showUserColumn && (
                    <TableCell>
                      <Box
                        component="a"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/manage-users/detail", { state: { user: row.user } });
                        }}
                        className="user_block click_action"
                      >
                        <p>
                          <b>{row.user?.fullName || "N/A"}</b>
                          <span>{row.user?.email}</span>
                          <span>{row.user?.phone}</span>
                        </p>
                      </Box>
                    </TableCell>
                  )}
                  {showPromotionColumn && (
                    <TableCell>
                      {row.orderId || "N/A"}
                    </TableCell>
                  )}
                  <TableCell>{row.score}</TableCell>
                  <TableCell>{row.reviews}</TableCell>
                  <TableCell>
                    {row.createdAt
                      ? new Date(row.createdAt).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                      : "N/A"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 0 && (
        <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
          <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
            Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Pagination
              module={ratings}
              page={page}
              onPageChange={setPage}
              totalPages={totalPages}
              setPage={setPage}
              totalCount={totalCount}
              limit={limit}
            />
          </div>
        </div>
      )}
    </>
  );
}
